import React, { useEffect, useRef } from "react";
import { IFullWidthExpanderProps } from "../FullWidthExpander";
import useExpander from "../Hooks/useExpander";
import classNames from "classnames";
import styled, { keyframes } from "styled-components";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import IconBranch from "../../icons/icon-branch.svg";
import IconAtm from "../../icons/icon-atm.svg";
import spinner from "../../icons/spinnersvg.svg";
import { useIsMobile } from "../../context/MobileContext";
import { AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../utilities/automation";
import { FeatureFlags, getFeatureFlag } from "../../utilities/featureFlags";
import { getTranslation } from "./translations";

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const ExpanderContainer = styled.div`

    .spinner {
        -webkit-mask: url(${spinner}) no-repeat 100% 100%;
        mask: url(${spinner}) no-repeat 100% 100%;
        -webkit-mask-size: auto;
        mask-position: right;
        mask-size: auto;
        background-color: ${props => props.theme.primary};
        -webkit-animation: spin 2s infinite linear;
        animation: ${spin} 2s infinite linear;
        width: 32px;
        height: 32px;
    }

    .expander-content {
        transition: height .2s ease;
        border-bottom: 1px solid #eaeaea;
        background-color: #fafafa;
    }

    .loading-tab {
        cursor: pointer;

        .spinner{
            position:relative;
            left:43%;
        }
    }

    .loading-tab:focus {
        outline: none;
    }

    .loading-tab.active:hover {
        background-color: ${props => props.theme.primary} !important;

        .spinner {
            background-color: #fff;
        }
    }

    .expander-tab {
        cursor: pointer;
        padding: 20px 10px;

        &.more-btn{
            height: 96px;
        }

        .item-title-icon {
            -webkit-mask: url(${IconBranch}) no-repeat 100% 100%;
            mask: url(${IconBranch}) no-repeat 100% 100%;
        }

        .item-atm-icon {
            -webkit-mask: url(${IconAtm}) no-repeat 100% 100%;
            mask: url(${IconAtm}) no-repeat 100% 100%;
        }

        .item-title-icon, .item-atm-icon {
            -webkit-mask-size: auto;
            mask-size: auto;
            background-color: #6b6b6b;
            height: 20px;
            width: 20px;
            margin-top: 5px;
            margin-bottom: 5px;
        }

        .branch-status {
            color: #1e1e1e;
        }

        .title, .branch-status {
            line-height: 1;
            font-size: 14.7px;
        }

        .title-address, .sub-title {
            font-size: 0.667rem;
            color: #1e1e1e;

            &:hover {
                text-decoration: none !important;
            }
        }

        .btn-circle-sm {
            width: 26px;
            height: 26px;
            padding: 0;
            border-radius: 50%;
            svg {
                margin: auto;
            }
        }

        &:hover {
            background-color: ${props => props.theme.primary} !important;

            .spinner {
                background-color: #fff;
            }

            .branch-status {
                color: #fff !important;
            }

            .btn {
                border-color: #fff;
                color: #fff;
            }

            .item-title-container, .branch-status {
                color: #fff !important;

                .title-address, .sub-title {
                    color: #fff !important;
                }

                .item-title-icon, .item-atm-icon {
                    background-color: #fff;
                }
            }
        }

        &.open, &.open:hover {
            background-color: #e9e9e9 !important;

            .branch-status {
                color: #1e1e1e !important;
            }

            .item-title-icon, .item-atm-icon {
                background-color: ${props => props.theme.primary} !important;
            }

            .title {
                 color: ${props => props.theme.primary} !important;
            }

            .title-address, .sub-title {
                color: #757575 !important;
            }
        }
    }
`;

function LoadingTabLabel({ label }) {
    return (
        <small data-test-id={createDataTestId({feature: AutomationTestIdFeature.BRANCH_LOCATOR, element: AutomationTestIdElementType.CONTAINER, specifier: 'noResults'})}>
            <div>{label.header}</div>
            {label.subHeader && <div>{label.subHeader}</div>}
        </small>
    );
}

export function LoadingTab({ showSpinner, label, onClickOrBlur }) {
    return (
        <ExpanderContainer >
            <button onClick={() => onClickOrBlur(true)} onBlur={() => onClickOrBlur(false)} className={classNames({ "active": showSpinner }, "w-100 border-0 loading-tab bg-gray-100 px-3 py-4")}>
                <div className="col-12 item-title-container">
                    {showSpinner ?
                        <div className="w-100 spinner-container text-center">
                            <div className="spinner"></div>
                        </div>
                        :
                        <LoadingTabLabel label={label} />
                    }
                </div>
            </button>
        </ExpanderContainer>
    )
}

export function LoadMoreTab({ showSpinner, onClick, loadMoreLabel }) {
    return (
        <ExpanderContainer onClick={onClick}>
            <div className={classNames("expander-tab bg-gray-100 text-center row align-items-center justify-content-center px-3 py-4 more-btn")}>
                {showSpinner
                    ?
                    <div className="spinner" >&nbsp;</div>
                    :
                    <>
                        <div className="col-12 item-title-container text-primary">
                            <h6 className="title">{loadMoreLabel}</h6>
                        </div>
                        <div className="col-12">
                            <Button variant="outline-primary" className="btn-circle-sm text-center d-flex m-auto">
                                <FontAwesomeIcon icon={faPlus} />
                            </Button>
                        </div>
                    </>
                }
            </div>
        </ExpanderContainer>
    );
}

interface IBranchExpanderProps extends IFullWidthExpanderProps {
    id: string;
    title: string;
    index?: number;
    itemIconClass?: string;
    street: string;
    distance: string;
    waitTime: string;
    isBranch: boolean;
    isBranchOpen: string;
    isDriveUpOpen: string;
    opensAt: string;
    selectedId?: string;
    lang: string;
    onSelect: (id: string) => void;
}

function BranchExpander(props: IBranchExpanderProps) {
    const { open, setOpen, height, ref } = useExpander(props.open);
    const scrollRef = useRef<HTMLDivElement>();
    const headerHeight = 129;
    const mobileHeight = 264;
    const smallHeaderHeight = 61;
    const { isMobileWidth } = useIsMobile();
    const enableBranchesWaitTimes = getFeatureFlag(FeatureFlags.ENABLE_BRANCHES_WAIT_TIMES);
    const translations = getTranslation(props.lang);

    useEffect(() => {
        if (props.selectedId == props.id) {
            setOpen(true);
            animateBranchList();
        }

    }, [props.selectedId]);

    useEffect(() => {
        if (open && typeof props.onOpen === "function") {
            props.onOpen(props.id);
        }
    }, [open]);

    function animateBranchList() {
        setTimeout(() => {
            if (scrollRef.current) {
                const rect = scrollRef.current.getBoundingClientRect();

                if (!isInViewport(rect) && !isLargerThanViewport(rect)) {
                    const height = isMobileWidth ? mobileHeight : smallHeaderHeight;

                    if (rect.top <= height) {
                        if (props.index === 0) {
                            scrollTo(document.documentElement.scrollTop, !isMobileWidth ? 0 : headerHeight, 500);
                        } else {
                            scrollTo(document.documentElement.scrollTop, document.documentElement.scrollTop + (rect.top - height) + 3, 500);
                        }
                    }

                    if (rect.bottom >= window.innerHeight) {
                        scrollTo(document.documentElement.scrollTop, document.documentElement.scrollTop + (rect.top + rect.height - window.innerHeight), 500);
                    }
                }
            }
        }, 500)
    }

    /**
     * scroll to the div that was selected in the map
     */
    function scrollTo(startValue, endValue, duration, onDone?) {
        // return a value between start and end as l goes from 0 to 1
        function lerp(start, end, l) {
            return start + (end - start) * l;
        }

        const startTime = performance.now();

        function animateScroll(now) {
            const timeSinceStart = (now - startTime);

            // l goes from 0 to 1 over durationInSeconds;
            const l = Math.min(timeSinceStart / duration, 1);
            window.scrollTo(window.scrollX, lerp(startValue, endValue, l));

            // if we haven't finished request another animation frame
            if (l < 1) {
                requestAnimationFrame(animateScroll);
            } else {
                if (typeof onDone === "function") {
                    onDone();
                }
            }
        }
        requestAnimationFrame(animateScroll);
    }

    /**
     * make sure the open container is not taller than the window which
     * will create a neverending loop. if it is taller just let it go
     * @param rect
     */
    function isLargerThanViewport(rect) {
        let height = isMobileWidth ? mobileHeight : headerHeight;
        return (
            rect.top < height &&
            rect.bottom > (window.innerHeight || document.documentElement.clientHeight)
        );
    }

    /**
     * check to see if the current div ref is in the viewport
     * @param rect
     */
    function isInViewport(rect) {
        let height = isMobileWidth ? mobileHeight : headerHeight;

        return (
            rect.top >= height &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        );
    }

    function handleHeaderClick(): void {
        if (!open) {
            setOpen(true);

            if (typeof props.onSelect === "function") {
                props.onSelect(props.id);
            }
        }
    }

    // Gets the status of the branch
    function getBranchStatus(): string {
        if (!enableBranchesWaitTimes || !props.isBranch) {
            return `${props.distance} Mi.`;
        } else if (props.waitTime !== null && props.isBranchOpen) { // Checks against null as waitTime could be 0
            return `${props.waitTime} min`;
        } else if (props.isBranchOpen) {
            return translations.open;
        } else if (props.isDriveUpOpen) {
            return translations.driveThruOpen;
        }
        return translations.closed;
    }

    // Gets the sub title status of the branch
    function getBranchStatusSubtitle(): string {
        if (!enableBranchesWaitTimes || !props.isBranch) {
            return '';
        } else if (props.waitTime !== null && props.isBranchOpen) { // Checks against null as waitTime could be 0
            return translations.estimatedWait;
        } else if (props.isBranchOpen) {
            return '';
        } else if (props.isDriveUpOpen) {
            return translations.lobbyClosed;
        } else if (props.opensAt) {
            return `${translations.opens} ${props.opensAt}`;
        }
        return '';
    }

    return (
        <ExpanderContainer id={props.id} ref={scrollRef} className={props.className} onClick={handleHeaderClick}>
            <div className={classNames("expander-tab align-items-center row", {
                "open bg-gray-200": open,
                "bg-gray-100": !open
                })}
                data-test-id={createDataTestId({feature: AutomationTestIdFeature.BRANCH_LOCATOR, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: 'mapEntry'})}>
                <div className="col item-title-container text-primary">
                    <div className={classNames(props.itemIconClass, "float-left mr-3")}>
                    </div>
                    <div className="title">
                        <strong>{props.title}</strong>
                    </div>
                    <div className="title-address">
                        {props.street}
                    </div>
                </div>
                <div className="col-auto branch-status px-1 text-right">
                        <div className="title">
                            <strong className="branch-status-title">{getBranchStatus()}</strong>
                        </div>
                        <div className="branch-status-subtitle sub-title">
                            {getBranchStatusSubtitle()}
                        </div>
                </div>
            </div>
            <div ref={ref}
                className={
                    classNames(
                        "expander-content",
                        "overflow-hidden",
                        { "content-visible": open }
                    )
                }
                style={{ height: height }}>
                <div className="p-3 d-inline-block">
                    {props.children}
                </div>
            </div>
        </ExpanderContainer>
    );
}

export default BranchExpander;